"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];
const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];

export function FadeUp({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduced = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced ? undefined : { opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: reduced ? 0 : 0.7, delay: reduced ? 0 : delay, ease: EASE_OUT }}
    >
      {children}
    </motion.div>
  );
}

export function SlideIn({
  children,
  from = "left",
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  from?: "left" | "right";
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduced = useReducedMotion();
  const x = from === "left" ? -60 : 60;
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced ? undefined : { opacity: 0, x }}
      animate={inView ? { opacity: 1, x: 0 } : undefined}
      transition={
        reduced
          ? { duration: 0 }
          : { duration: 0.8, delay, type: "spring", damping: 25, stiffness: 120 }
      }
    >
      {children}
    </motion.div>
  );
}

export function Reveal({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduced = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced ? undefined : { opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: reduced ? 0 : 0.8, ease: EASE_OUT_EXPO }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Staggered container: children animate in sequence with configurable delay.
 * Wrap child elements in motion.div with variants for the cascade effect.
 */
const staggerContainer = {
  hidden: {},
  visible: (stagger: number) => ({
    transition: { staggerChildren: stagger, delayChildren: 0.1 },
  }),
};

const staggerChild = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: EASE_OUT },
  },
};

export function StaggerGroup({
  children,
  stagger = 0.08,
  className = "",
}: {
  children: React.ReactNode;
  stagger?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduced = useReducedMotion();

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={staggerContainer}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      custom={stagger}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div className={className} variants={staggerChild}>
      {children}
    </motion.div>
  );
}

/**
 * Word-by-word text reveal for headlines.
 * Splits text into words and staggers their entrance.
 */
export function TextReveal({
  text,
  className = "",
  style,
  stagger = 0.04,
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  stagger?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduced = useReducedMotion();
  const words = text.split(" ");

  if (reduced) {
    return (
      <span ref={ref} className={className} style={style}>
        {text}
      </span>
    );
  }

  return (
    <motion.span
      ref={ref}
      className={className}
      style={{ ...style, display: "inline" }}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: 0.05 } },
      }}
    >
      {words.map((word, i) => (
        <motion.span
          key={i}
          style={{ display: "inline-block", whiteSpace: "pre" }}
          variants={{
            hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
            visible: {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: { duration: 0.5, ease: EASE_OUT },
            },
          }}
        >
          {word}{i < words.length - 1 ? " " : ""}
        </motion.span>
      ))}
    </motion.span>
  );
}

/**
 * Clip-path reveal: wipes content in from one direction.
 */
export function ClipReveal({
  children,
  from = "left",
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  from?: "left" | "right" | "bottom";
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px" });
  const reduced = useReducedMotion();

  const clipPaths = {
    left: { hidden: "inset(0 100% 0 0)", visible: "inset(0 0% 0 0)" },
    right: { hidden: "inset(0 0 0 100%)", visible: "inset(0 0 0 0%)" },
    bottom: { hidden: "inset(0 0 100% 0)", visible: "inset(0 0 0% 0)" },
  };

  const clip = clipPaths[from];

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ clipPath: clip.hidden, opacity: 0 }}
      animate={
        inView
          ? { clipPath: clip.visible, opacity: 1 }
          : undefined
      }
      transition={{ duration: 0.8, delay, ease: EASE_OUT_EXPO }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Gradient bridge between sections with different backgrounds.
 * Creates a smooth 120-200px transition zone.
 */
export function SectionBridge({
  from,
  to,
  height = 160,
  className = "",
}: {
  from: string;
  to: string;
  height?: number;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        height,
        background: `linear-gradient(to bottom, ${from} 0%, ${to} 100%)`,
        marginTop: -1,
        marginBottom: -1,
        pointerEvents: "none",
      }}
    />
  );
}
